import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { API_URL } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  MapPin, Plus, Building, Layers, RefreshCw, Users, Shield,
  Trash2, AlertTriangle, ChevronRight, Edit
} from 'lucide-react';

const API = API_URL;

export default function SitesZonesPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { effectiveTenantId } = useTenant();
  const navigate = useNavigate();
  const fr = language === 'fr';

  const [sites, setSites] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {type, id, name}
  const [editingSite, setEditingSite] = useState(null);
  const [editingZone, setEditingZone] = useState(null);

  // Forms
  const [siteName, setSiteName] = useState('');
  const [zoneData, setZoneData] = useState({ name: '', description: '', siteId: '' });

  const isSupervisor = user?.role === 'tenant_supervisor';
  const canManage = !isSupervisor;

  /* ========== FETCH ========== */
  const fetchData = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const [sitesRes, zonesRes] = await Promise.all([
        axios.get(`${API}/sites/stats?tenantId=${effectiveTenantId}`),
        axios.get(`${API}/zones/stats?tenantId=${effectiveTenantId}`)
      ]);
      setSites(sitesRes.data);
      setZones(zonesRes.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
      toast.error(fr ? 'Erreur de chargement' : 'Loading error');
    } finally { setLoading(false); }
  }, [effectiveTenantId, fr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ========== KPIs ========== */
  const kpis = useMemo(() => {
    const totalEmployees = zones.reduce((s, z) => s + (z.employeeCount || 0), 0);
    const totalSupervisors = zones.reduce((s, z) => s + (z.supervisorCount || 0), 0);
    return { sites: sites.length, zones: zones.length, employees: totalEmployees, supervisors: totalSupervisors };
  }, [sites, zones]);

  /* ========== HELPERS ========== */
  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.name || '-';

  /* ========== SITE ACTIONS ========== */
  const handleAddSite = async (e) => {
    e.preventDefault();
    if (!effectiveTenantId) return;
    try {
      await axios.post(`${API}/sites`, { tenantId: effectiveTenantId, name: siteName });
      toast.success(fr ? 'Site créé' : 'Site created');
      setSiteDialogOpen(false); setSiteName(''); fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (fr ? 'Erreur' : 'Error'));
    }
  };

  const openDeleteDialog = (type, id, name, count) => {
    setDeleteTarget({ type, id, name, count });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const url = deleteTarget.type === 'site' ? `${API}/sites/${deleteTarget.id}` : `${API}/zones/${deleteTarget.id}`;
    try {
      await axios.delete(url);
      toast.success(fr ? `${deleteTarget.type === 'site' ? 'Site' : 'Zone'} supprimé(e)` : `${deleteTarget.type} deleted`);
      setDeleteDialogOpen(false); setDeleteTarget(null); fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (fr ? 'Erreur' : 'Error'));
    }
  };

  /* ========== ZONE ACTIONS ========== */
  const handleAddZone = async (e) => {
    e.preventDefault();
    if (!zoneData.siteId || !effectiveTenantId) return;
    try {
      await axios.post(`${API}/zones`, {
        tenantId: effectiveTenantId, siteId: zoneData.siteId, name: zoneData.name, description: zoneData.description
      });
      toast.success(fr ? 'Zone créée' : 'Zone created');
      setZoneDialogOpen(false); setZoneData({ name: '', description: '', siteId: '' }); fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (fr ? 'Erreur' : 'Error'));
    }
  };

  const navigateToEmployees = (zoneId) => {
    navigate(`/admin/employees?zone=${zoneId}`);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="animate-spin text-primary" size={32} /></div>;

  return (
    <div className="space-y-4" data-testid="sites-zones-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{fr ? 'Sites & Zones' : 'Sites & Zones'}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{fr ? 'Infrastructure et découpage géographique' : 'Infrastructure and geographic layout'}</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sz-kpis">
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-blue-50/50 dark:bg-blue-950/20">
          <Building size={18} className="text-blue-600 dark:text-blue-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.sites}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Sites' : 'Sites'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-green-50/50 dark:bg-green-950/20">
          <Layers size={18} className="text-green-600 dark:text-green-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.zones}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Zones' : 'Zones'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-purple-50/50 dark:bg-purple-950/20">
          <Users size={18} className="text-purple-600 dark:text-purple-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.employees}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Employés' : 'Employees'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-amber-50/50 dark:bg-amber-950/20">
          <Shield size={18} className="text-amber-600 dark:text-amber-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.supervisors}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Superviseurs' : 'Supervisors'}</p></div>
        </div>
      </div>

      {/* Sites */}
      <Card className="dark:border-slate-700" data-testid="sites-card">
        <CardHeader className="py-2 px-4 border-b dark:border-slate-700 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2 text-slate-700 dark:text-slate-300"><Building size={16} /> {fr ? 'Sites' : 'Sites'}</CardTitle>
          {canManage && (
            <Button size="sm" className="gap-1.5 h-8 text-xs" onClick={() => { setSiteName(''); setSiteDialogOpen(true); }} data-testid="add-site-btn">
              <Plus size={14} /> {fr ? 'Ajouter site' : 'Add Site'}
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80 dark:bg-slate-800/80">
                <TableHead className="text-xs font-bold py-2">{fr ? 'Nom' : 'Name'}</TableHead>
                <TableHead className="text-xs font-bold py-2 text-center">{fr ? 'Zones' : 'Zones'}</TableHead>
                <TableHead className="text-xs font-bold py-2 text-center">{fr ? 'Employés' : 'Employees'}</TableHead>
                <TableHead className="text-xs font-bold py-2">{fr ? 'Créé le' : 'Created'}</TableHead>
                {canManage && <TableHead className="text-xs font-bold py-2 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites.length === 0 ? (
                <TableRow><TableCell colSpan={canManage ? 5 : 4} className="text-center py-8 text-slate-400 dark:text-slate-500">{fr ? 'Aucun site' : 'No sites'}</TableCell></TableRow>
              ) : (
                sites.map((site, idx) => (
                  <TableRow key={site.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 ${idx % 2 === 1 ? 'bg-slate-25 dark:bg-slate-900/30' : ''}`} data-testid={`site-row-${site.id}`}>
                    <TableCell className="py-2 font-medium text-sm text-slate-900 dark:text-slate-100">{site.name}</TableCell>
                    <TableCell className="py-2 text-center"><Badge variant="outline" className="text-[10px]">{site.zoneCount || 0}</Badge></TableCell>
                    <TableCell className="py-2 text-center"><Badge className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 text-[10px]">{site.employeeCount || 0}</Badge></TableCell>
                    <TableCell className="py-2 text-sm text-slate-500 dark:text-slate-400">{new Date(site.createdAt).toLocaleDateString(fr ? 'fr-FR' : 'en-US')}</TableCell>
                    {canManage && (
                      <TableCell className="py-2 text-right">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
                          onClick={() => openDeleteDialog('site', site.id, site.name, (site.zoneCount || 0) + (site.employeeCount || 0))}
                          data-testid={`delete-site-${site.id}`}><Trash2 size={14} /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Zones */}
      <Card className="dark:border-slate-700" data-testid="zones-card">
        <CardHeader className="py-2 px-4 border-b dark:border-slate-700 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2 text-slate-700 dark:text-slate-300"><Layers size={16} /> {fr ? 'Zones' : 'Zones'}</CardTitle>
          {canManage && (
            <Button size="sm" className="gap-1.5 h-8 text-xs" onClick={() => { setZoneData({ name: '', description: '', siteId: '' }); setZoneDialogOpen(true); }} disabled={sites.length === 0} data-testid="add-zone-btn">
              <Plus size={14} /> {fr ? 'Ajouter zone' : 'Add Zone'}
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80 dark:bg-slate-800/80">
                <TableHead className="text-xs font-bold py-2">{fr ? 'Nom' : 'Name'}</TableHead>
                <TableHead className="text-xs font-bold py-2">{fr ? 'Site' : 'Site'}</TableHead>
                <TableHead className="text-xs font-bold py-2">{fr ? 'Description' : 'Description'}</TableHead>
                <TableHead className="text-xs font-bold py-2 text-center">{fr ? 'Employés' : 'Employees'}</TableHead>
                <TableHead className="text-xs font-bold py-2 text-center">{fr ? 'Superviseurs' : 'Supervisors'}</TableHead>
                {canManage && <TableHead className="text-xs font-bold py-2 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {zones.length === 0 ? (
                <TableRow><TableCell colSpan={canManage ? 6 : 5} className="text-center py-8 text-slate-400 dark:text-slate-500">{fr ? 'Aucune zone' : 'No zones'}</TableCell></TableRow>
              ) : (
                zones.map((zone, idx) => (
                  <TableRow key={zone.id}
                    className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 ${idx % 2 === 1 ? 'bg-slate-25 dark:bg-slate-900/30' : ''}`}
                    onClick={() => navigateToEmployees(zone.id)}
                    data-testid={`zone-row-${zone.id}`}>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-1.5">
                        <MapPin size={14} className="text-primary" />
                        <span className="font-medium text-sm text-slate-900 dark:text-slate-100">{zone.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2 text-sm text-slate-600 dark:text-slate-400">{getSiteName(zone.siteId)}</TableCell>
                    <TableCell className="py-2 text-sm text-slate-500 dark:text-slate-400 max-w-xs truncate">{zone.description || '-'}</TableCell>
                    <TableCell className="py-2 text-center">
                      <Badge className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 text-[10px] cursor-pointer hover:bg-blue-200">
                        <Users size={10} className="mr-0.5" />{zone.employeeCount || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      {(zone.supervisorCount || 0) > 0 ? (
                        <Badge className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-[10px]">
                          <Shield size={10} className="mr-0.5" />{zone.supervisorCount}
                        </Badge>
                      ) : <span className="text-slate-400 text-xs">-</span>}
                    </TableCell>
                    {canManage && (
                      <TableCell className="py-2 text-right" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
                          onClick={() => openDeleteDialog('zone', zone.id, zone.name, (zone.employeeCount || 0) + (zone.supervisorCount || 0))}
                          data-testid={`delete-zone-${zone.id}`}><Trash2 size={14} /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Site Dialog */}
      <Dialog open={siteDialogOpen} onOpenChange={setSiteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building size={18} /> {fr ? 'Ajouter site' : 'Add Site'}</DialogTitle></DialogHeader>
          <form onSubmit={handleAddSite}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{fr ? 'Nom du site' : 'Site Name'} *</Label>
                <Input value={siteName} onChange={e => setSiteName(e.target.value)} placeholder={fr ? 'Usine principale' : 'Main factory'} required data-testid="site-name-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSiteDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
              <Button type="submit" data-testid="save-site-btn">{fr ? 'Créer' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Zone Dialog */}
      <Dialog open={zoneDialogOpen} onOpenChange={setZoneDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers size={18} /> {fr ? 'Ajouter zone' : 'Add Zone'}</DialogTitle></DialogHeader>
          <form onSubmit={handleAddZone}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{fr ? 'Site' : 'Site'} *</Label>
                <Select value={zoneData.siteId} onValueChange={v => setZoneData({ ...zoneData, siteId: v })}>
                  <SelectTrigger data-testid="zone-site-select"><SelectValue placeholder={fr ? 'Sélectionner un site' : 'Select a site'} /></SelectTrigger>
                  <SelectContent>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{fr ? 'Nom de la zone' : 'Zone Name'} *</Label>
                <Input value={zoneData.name} onChange={e => setZoneData({ ...zoneData, name: e.target.value })} placeholder={fr ? 'Ligne Production A' : 'Production Line A'} required data-testid="zone-name-input" />
              </div>
              <div className="space-y-2">
                <Label>{fr ? 'Description' : 'Description'}</Label>
                <Textarea value={zoneData.description} onChange={e => setZoneData({ ...zoneData, description: e.target.value })} placeholder={fr ? 'Description...' : 'Description...'} rows={2} data-testid="zone-description-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setZoneDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
              <Button type="submit" data-testid="save-zone-btn">{fr ? 'Créer' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={18} /> {fr ? 'Confirmer la suppression' : 'Confirm Delete'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {fr
                ? `Voulez-vous supprimer ${deleteTarget?.type === 'site' ? 'le site' : 'la zone'} "${deleteTarget?.name}" ?`
                : `Delete ${deleteTarget?.type} "${deleteTarget?.name}"?`}
            </p>
            {(deleteTarget?.count || 0) > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                  <AlertTriangle size={12} className="inline mr-1" />
                  {fr
                    ? `Attention: ${deleteTarget?.count} élément(s) référence(nt) ce ${deleteTarget?.type}. La suppression sera bloquée par le serveur.`
                    : `Warning: ${deleteTarget?.count} item(s) reference this ${deleteTarget?.type}. Deletion will be blocked by the server.`}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete} data-testid="confirm-delete-btn">{fr ? 'Supprimer' : 'Delete'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
